/**
 * AI Job polling — the client half of the async Nano Banana pipeline.
 *
 * Backend routes like /beauty/preview, /tryon, /atelier/…/tryon and
 * /shopping/recommendation-image now return 202 { job_id } instead of the
 * synchronous result (Cloudflare's 100s cap was killing them mid-flight).
 * We poll GET /api/ai-jobs/{job_id} until status is "completed" or "failed".
 *
 *   const result = await pollAiJob(jobId, { onTick, signal });
 *   // result === job.result (kind-specific payload)
 *
 * `onTick(elapsedMs)` fires every poll so callers can drive a progress bar.
 * Times out after 180s (matches confirmed user preference in fork plan).
 */
import { api } from "@/lib/api";

const DEFAULT_INTERVAL_MS = 2000;
const DEFAULT_TIMEOUT_MS = 180_000;

class AiJobError extends Error {
  constructor(message, { status, jobId } = {}) {
    super(message);
    this.name = "AiJobError";
    this.status = status;
    this.jobId = jobId;
  }
}

export async function pollAiJob(
  jobId,
  { intervalMs = DEFAULT_INTERVAL_MS, timeoutMs = DEFAULT_TIMEOUT_MS, onTick, signal } = {},
) {
  if (!jobId) throw new AiJobError("Missing job_id");
  const started = Date.now();
  while (true) {
    if (signal?.aborted) throw new AiJobError("Cancelled", { jobId });
    const elapsed = Date.now() - started;
    if (elapsed > timeoutMs) {
      throw new AiJobError("This is taking longer than usual — please try again.", { jobId });
    }
    onTick?.(elapsed);
    const { data } = await api.get(`/ai-jobs/${jobId}`);
    if (data.status === "completed") return data.result || {};
    if (data.status === "failed") {
      throw new AiJobError(data.error || "AI job failed", { status: "failed", jobId });
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/**
 * Convenience wrapper: fires a POST that (may) return { job_id, status } and
 * polls to completion. Handles two shapes so callers can keep working with
 * legacy cached responses:
 *   • { job_id, status: "queued" }  → poll to completion
 *   • { image_path, url, cached: true } (or any non-job payload) → returned as-is
 */
export async function runAiJob(requestFn, { onTick, signal, timeoutMs, intervalMs } = {}) {
  const { data } = await requestFn();
  if (data && data.job_id && data.status === "queued") {
    return pollAiJob(data.job_id, { onTick, signal, timeoutMs, intervalMs });
  }
  return data;
}

export { AiJobError };
